import * as constants from "../constants/constants";
import { Responsibility } from '../types/responsibility.type';

// --- New typed interfaces ---

export interface TransportStats {
    waiting: number;
    inTransit: number;
    delivered: number;
    withoutTransport: number;
    withoutTransportWaiting: number;
    withoutTransportDone: number;
    cities: Array<{ city: string; count: number }>;
    drivers: Array<{ id: number; name: string; count: number }>;
}

export interface TransportListItem {
    id: number;
    code: string;
    status: 1 | 2;
    statusLabel: string;
    driver: { id: number; name: string } | null;
    departureDate: string | null;
    arrivalDate: string | null;
    duration: string | null;
    address: string | null;
    itemCount: number;
    clientNames: string;
    projectNames: string;
    createdAt: string;
}

export interface TransportDetail {
    id: number;
    code: string;
    status: 1 | 2;
    statusLabel: string;
    driver: { id: number; name: string } | null;
    createdBy: { id: number; name: string } | null;
    departureDate: string | null;
    estimatedArrivalDate: string | null;
    arrivalDate: string | null;
    duration: string | null;
    address: string | null;
    coordinates: string | null;
    notes: string | null;
    isMultiStop: boolean;
    itemCount: number;
    items: TransportItemDetail[];
    checklist: ChecklistItem[];
    comments: Array<{ id: number; text: string; user: { id: number; name: string } | null; createdAt: string | null }>;
    timeline: TimelineEntry[];
    createdAt: string;
}

export interface TransportItemDetail {
    id: number;
    warehouseRecord: { id: number; label: string; products: any[] } | null;
    project: { id: number; name: string } | null;
    client: { id: number; name: string } | null;
    address: string | null;
    coordinates: string | null;
    deliveryConfirmedAt: string | null;
    confirmedBy: { id: number; name: string } | null;
    deliveryNotes: string | null;
    sortOrder: number;
    installation: any | null;
}

export interface ChecklistItem {
    id: number;
    label: string;
    isChecked: boolean;
    checkedBy: { id: number; name: string } | null;
    checkedAt: string | null;
}

export interface TimelineEntry {
    id: number;
    user: { id: number; name: string } | null;
    eventType: string;
    description: string;
    createdAt: string;
}

export interface Pagination {
    currentPage: number;
    lastPage: number;
    perPage: number;
    total: number;
}

export interface TransportState {
    // New state
    stats: TransportStats | null;
    transports: TransportListItem[];
    transportsPagination: Pagination;
    currentTransport: TransportDetail | null;
    availableItems: any[];
    availableItemsPagination: Pagination;
    withoutTransportItems: any[];
    withoutTransportPagination: Pagination;
    drivers: Array<{ id: number; name: string }>;
    locations: any[];
    timeline: TimelineEntry[];
    // Legacy state (kept for backward compat)
    equipments: Responsibility[];
    transportEquipments: any;
    selectedWarehouseRecordForTransport: any;
}

const defaultPagination: Pagination = {
    currentPage: 1,
    lastPage: 1,
    perPage: 20,
    total: 0,
};

const parsePagination = (meta: any): Pagination => {
    if (!meta) return { ...defaultPagination };
    return {
        currentPage: Number(meta.current_page) || 1,
        lastPage: Number(meta.last_page) || 1,
        perPage: Number(meta.per_page) || 20,
        total: Number(meta.total) || 0,
    };
};

const initialState = (): TransportState => {
    return {
        // New state
        stats: null,
        transports: [],
        transportsPagination: { ...defaultPagination },
        currentTransport: null,
        availableItems: [],
        availableItemsPagination: { ...defaultPagination },
        withoutTransportItems: [],
        withoutTransportPagination: { ...defaultPagination },
        drivers: [],
        locations: [],
        timeline: [],
        // Legacy
        equipments: [],
        transportEquipments: [],
        selectedWarehouseRecordForTransport: {},
    };
};

const transport = (state: TransportState = initialState(), action: any) => {
    switch (action.type) {
        // --- New actions ---

        case constants.FETCH_TRANSPORT_DASHBOARD_STATS: {
            return {
                ...state,
                stats: action.payload,
            };
        }

        case constants.FETCH_TRANSPORTS: {
            return {
                ...state,
                transports: action.payload.data || [],
                transportsPagination: parsePagination(action.payload.meta),
            };
        }

        case constants.FETCH_TRANSPORT: {
            return {
                ...state,
                currentTransport: action.payload,
            };
        }

        case constants.FETCH_AVAILABLE_TRANSPORT_ITEMS: {
            return {
                ...state,
                availableItems: action.payload.data || [],
                availableItemsPagination: parsePagination(action.payload.meta),
            };
        }

        case constants.FETCH_WITHOUT_TRANSPORT: {
            const data = action.payload;
            return {
                ...state,
                withoutTransportItems: data?.data || [],
                withoutTransportPagination: data?.meta ? parsePagination(data.meta) : (data?.last_page ? parsePagination(data) : { ...defaultPagination, total: (data?.data || []).length }),
            };
        }

        case constants.FETCH_TRANSPORT_DRIVERS: {
            return {
                ...state,
                drivers: action.payload || [],
            };
        }

        case constants.FETCH_TRANSPORT_LOCATIONS: {
            return {
                ...state,
                locations: action.payload || [],
            };
        }

        case constants.FETCH_TRANSPORT_TIMELINE: {
            return {
                ...state,
                timeline: action.payload || [],
            };
        }

        // --- Legacy actions ---

        case constants.FETCH_EQUIPMENTS: {
            return {
                ...state,
                equipments: action.payload,
            };
        }

        case constants.FETCH_TRANSPORT_EQUIPMENTS: {
            let equipments: any[] = [];
            action?.payload?.forEach((project: any) => {
                project.offers?.products?.forEach((product: any) => {
                    product.equipments?.forEach((equipment: any) => {
                        let tempObj = equipments.find(item => item.value === equipment.value);
                        if (!tempObj) {
                            equipments.push(equipment);
                        }
                    });
                });
            });
            return {
                ...state,
                transportEquipments: equipments,
            };
        }

        case constants.SET_SELECTED_WAREHOUSE_RECORD_FOR_TRANSPORT: {
            return {
                ...state,
                selectedWarehouseRecordForTransport: action.payload,
            };
        }
    }

    return state;
};

export default transport;
